import {
  PAGE_FOOTER_CLEARANCE_MM,
  PAGE_HEADER_HEIGHT_MM,
  PAGE_HEIGHT_MM,
  PAGE_MARGIN_MM,
  PAGE_WIDTH_MM,
} from "./pdfExportConstants";

export interface PdfBlockSize {
  id: string;
  widthMm: number;
  heightMm: number;
  splittable?: boolean;
  continueOnPage?: boolean;
}

export interface PdfRow {
  id: string;
  blocks: PdfBlockSize[];
}

export interface PlacedBlock {
  id: string;
  page: number;
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  sliceIndex: number;
  sliceCount: number;
}

export interface PdfPageLayout {
  pageWidthMm: number;
  pageHeightMm: number;
  marginMm: number;
  headerHeightMm: number;
  pages: PlacedBlock[][];
}

interface PlanPdfPagesOptions {
  pageWidthMm?: number;
  pageHeightMm?: number;
  marginMm?: number;
  headerHeightMm?: number;
}

const DEFAULT_PAGE_WIDTH_MM = PAGE_WIDTH_MM;
const DEFAULT_PAGE_HEIGHT_MM = PAGE_HEIGHT_MM;
const DEFAULT_MARGIN_MM = PAGE_MARGIN_MM;
const DEFAULT_HEADER_HEIGHT_MM = PAGE_HEADER_HEIGHT_MM;
const ROW_COLUMN_GAP_MM = 8;
const ROW_VERTICAL_GAP_MM = 6;
const MIN_TRAILING_SLICE_MM = 15;

interface ScaledBlock extends PdfBlockSize {
  widthMm: number;
  heightMm: number;
}

function scaleToFit(block: PdfBlockSize, maxWidthMm: number): ScaledBlock {
  const scale = block.widthMm > maxWidthMm ? maxWidthMm / block.widthMm : 1;
  return { ...block, widthMm: block.widthMm * scale, heightMm: block.heightMm * scale };
}

export function planPdfPages(
  rows: PdfRow[],
  options: PlanPdfPagesOptions = {},
): PdfPageLayout {
  const pageWidthMm = options.pageWidthMm ?? DEFAULT_PAGE_WIDTH_MM;
  const pageHeightMm = options.pageHeightMm ?? DEFAULT_PAGE_HEIGHT_MM;
  const marginMm = options.marginMm ?? DEFAULT_MARGIN_MM;
  const headerHeightMm = options.headerHeightMm ?? DEFAULT_HEADER_HEIGHT_MM;

  const usableWidthMm = pageWidthMm - marginMm * 2;
  const usableHeightMm = pageHeightMm - marginMm * 2 - headerHeightMm - PAGE_FOOTER_CLEARANCE_MM;

  const pages: PlacedBlock[][] = [];
  let pageIndex = 0;
  let cursorYMm = 0;

  const ensurePage = () => {
    while (pages.length <= pageIndex) {
      pages.push([]);
    }
  };

  if (rows.length === 0) {
    return { pageWidthMm, pageHeightMm, marginMm, headerHeightMm, pages };
  }

  ensurePage();

  const startFreshPage = () => {
    pageIndex += 1;
    cursorYMm = 0;
    ensurePage();
  };

  const placeSideBySide = (
    blocks: PdfBlockSize[],
    yMm: number,
  ): { heightMm: number } => {
    const columnWidthMm = blocks.length > 1
      ? (usableWidthMm - ROW_COLUMN_GAP_MM * (blocks.length - 1)) / blocks.length
      : usableWidthMm;
    const scaledBlocks = blocks.map(block => scaleToFit(block, columnWidthMm));
    const rowHeightMm = Math.max(...scaledBlocks.map(block => block.heightMm));

    let xMm = marginMm;
    for (const block of scaledBlocks) {

      const columnXMm = xMm + (columnWidthMm - block.widthMm) / 2;
      pages[pageIndex].push({
        id: block.id,
        page: pageIndex,
        xMm: columnXMm,
        yMm,
        widthMm: block.widthMm,
        heightMm: block.heightMm,
        sliceIndex: 0,
        sliceCount: 1,
      });
      xMm += columnWidthMm + ROW_COLUMN_GAP_MM;
    }
    return { heightMm: rowHeightMm };
  };

  const placeSplitStack = (blocks: PdfBlockSize[]) => {
    for (const rawBlock of blocks) {
      placeSingleBlock(rawBlock);
    }
  };

  function placeSingleBlock(rawBlock: PdfBlockSize) {
    const block = scaleToFit(rawBlock, usableWidthMm);

    const fitsOnOnePage = block.heightMm <= usableHeightMm;
    const remainingBeforeSplitMm = usableHeightMm - cursorYMm;
    const continueIntoRemainder = Boolean(block.splittable && block.continueOnPage)
      && cursorYMm > 0
      && block.heightMm > remainingBeforeSplitMm
      && remainingBeforeSplitMm >= MIN_TRAILING_SLICE_MM;

    if (fitsOnOnePage && !continueIntoRemainder) {
      const remainingHeightMm = remainingBeforeSplitMm;
      const placedBlock = block;
      if (cursorYMm > 0 && block.heightMm > remainingHeightMm) {
        startFreshPage();
      }
      const placedXMm = marginMm + (usableWidthMm - placedBlock.widthMm) / 2;
      pages[pageIndex].push({
        id: placedBlock.id,
        page: pageIndex,
        xMm: placedXMm,
        yMm: marginMm + headerHeightMm + cursorYMm,
        widthMm: placedBlock.widthMm,
        heightMm: placedBlock.heightMm,
        sliceIndex: 0,
        sliceCount: 1,
      });
      cursorYMm += placedBlock.heightMm + ROW_VERTICAL_GAP_MM;
      return;
    }

    const remainingOnPageMm = usableHeightMm - cursorYMm;
    const continueOnPage = continueIntoRemainder;

    if (cursorYMm > 0 && !continueOnPage) {
      startFreshPage();
    }

    if (!block.splittable) {
      const fittedScale = block.heightMm > usableHeightMm ? usableHeightMm / block.heightMm : 1;
      const widthMm = block.widthMm * fittedScale;
      const heightMm = block.heightMm * fittedScale;
      pages[pageIndex].push({
        id: block.id,
        page: pageIndex,
        xMm: marginMm + (usableWidthMm - widthMm) / 2,
        yMm: marginMm + headerHeightMm,
        widthMm,
        heightMm,
        sliceIndex: 0,
        sliceCount: 1,
      });
      startFreshPage();
      return;
    }

    const sliceHeightsMm: number[] = [];
    let unplacedHeightMm = block.heightMm;
    if (continueOnPage) {
      sliceHeightsMm.push(remainingOnPageMm);
      unplacedHeightMm -= remainingOnPageMm;
    }
    while (unplacedHeightMm > 0.5) {
      const sliceHeightMm = Math.min(usableHeightMm, unplacedHeightMm);
      const trailingMm = unplacedHeightMm - sliceHeightMm;
      if (trailingMm > 0 && trailingMm < MIN_TRAILING_SLICE_MM) {
        sliceHeightsMm.push(unplacedHeightMm);
        unplacedHeightMm = 0;
      } else {
        sliceHeightsMm.push(sliceHeightMm);
        unplacedHeightMm -= sliceHeightMm;
      }
    }
    const sliceCount = sliceHeightsMm.length;
    sliceHeightsMm.forEach((sliceHeightMm, sliceIndex) => {
      const yMm = sliceIndex === 0 && continueOnPage
        ? marginMm + headerHeightMm + (usableHeightMm - remainingOnPageMm)
        : marginMm + headerHeightMm;
      pages[pageIndex].push({
        id: block.id,
        page: pageIndex,
        xMm: marginMm,
        yMm,
        widthMm: block.widthMm,
        heightMm: sliceHeightMm,
        sliceIndex,
        sliceCount,
      });
      if (sliceIndex < sliceCount - 1) {
        startFreshPage();
      }
    });
    cursorYMm = sliceHeightsMm[sliceCount - 1] ?? 0;
  }

  for (const row of rows) {
    if (row.blocks.length === 0) {
      continue;
    }

    if (row.blocks.length === 1) {
      placeSingleBlock(row.blocks[0]);
      continue;
    }

    const columnWidthMm = (usableWidthMm - ROW_COLUMN_GAP_MM * (row.blocks.length - 1)) / row.blocks.length;
    const scaledForMeasurement = row.blocks.map(block => scaleToFit(block, columnWidthMm));
    const rowHeightMm = Math.max(...scaledForMeasurement.map(block => block.heightMm));

    if (rowHeightMm <= usableHeightMm) {
      const remainingHeightMm = usableHeightMm - cursorYMm;
      if (cursorYMm > 0 && rowHeightMm > remainingHeightMm) {
        startFreshPage();
      }
      placeSideBySide(row.blocks, marginMm + headerHeightMm + cursorYMm);
      cursorYMm += rowHeightMm + ROW_VERTICAL_GAP_MM;
      continue;
    }

    if (cursorYMm > 0) {
      startFreshPage();
    }
    placeSplitStack(row.blocks);
  }

  return { pageWidthMm, pageHeightMm, marginMm, headerHeightMm, pages };
}
